/**
 * KML 2.2 parser.
 *
 * Supports three common track encodings:
 *   1. `<Placemark><LineString><coordinates>lon,lat,alt lon,lat,alt ...</coordinates>`
 *   2. `<gx:Track>` with matched `<when>` + `<gx:coord>` pairs (Google Earth tour export)
 *   3. `<Placemark><Point><coordinates>lon,lat,alt</coordinates>` per point
 *
 * Returns a flat array of TrackPoint regardless of which shape was used.
 */
import type { TrackPoint } from './track-types';

const M_TO_FT = 3.28084;

function parseCoordTriple(triple: string): { lon: number; lat: number; alt?: number } | null {
  const [lonStr, latStr, altStr] = triple.split(',');
  const lon = parseFloat(lonStr);
  const lat = parseFloat(latStr);
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;
  const alt = altStr !== undefined ? parseFloat(altStr) : NaN;
  return { lon, lat, alt: Number.isFinite(alt) ? alt : undefined };
}

function pointsFromCoordinatesBlob(blob: string, timestamps: number[] = []): TrackPoint[] {
  const out: TrackPoint[] = [];
  const triples = blob.trim().split(/\s+/);
  triples.forEach((triple, i) => {
    const coord = parseCoordTriple(triple);
    if (!coord) return;
    out.push({
      timestamp: timestamps[i],
      latitude: coord.lat,
      longitude: coord.lon,
      altitudeFt: coord.alt !== undefined ? coord.alt * M_TO_FT : undefined,
    });
  });
  return out;
}

function parseGxTrack(track: Element): TrackPoint[] {
  const whens = Array.from(track.getElementsByTagName('when'));
  const coords = Array.from(track.getElementsByTagName('*')).filter(
    (n) => n.localName === 'coord',
  );
  const out: TrackPoint[] = [];
  const n = Math.min(whens.length, coords.length);
  for (let i = 0; i < n; i++) {
    const [lonStr, latStr, altStr] = (coords[i].textContent ?? '').trim().split(/\s+/);
    const lon = parseFloat(lonStr);
    const lat = parseFloat(latStr);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) continue;
    const alt = altStr !== undefined ? parseFloat(altStr) : NaN;
    const t = whens[i].textContent?.trim();
    const timestamp = t ? Date.parse(t) : undefined;
    out.push({
      timestamp: Number.isFinite(timestamp as number) ? (timestamp as number) : undefined,
      latitude: lat,
      longitude: lon,
      altitudeFt: Number.isFinite(alt) ? alt * M_TO_FT : undefined,
    });
  }
  return out;
}

export function parseKml(content: string): { name: string | undefined; points: TrackPoint[] } {
  const doc = new DOMParser().parseFromString(content, 'application/xml');
  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new Error('KML parse error: invalid XML');
  }

  const name = doc.getElementsByTagName('Document')[0]?.getElementsByTagName('name')[0]?.textContent
    ?? doc.getElementsByTagName('Folder')[0]?.getElementsByTagName('name')[0]?.textContent
    ?? undefined;

  const points: TrackPoint[] = [];

  // Prefer gx:Track (has timestamps)
  const gxTracks = Array.from(doc.getElementsByTagName('*')).filter((n) => n.localName === 'Track');
  for (const t of gxTracks) {
    points.push(...parseGxTrack(t));
  }

  // Otherwise use LineString(s)
  if (points.length === 0) {
    const lineStrings = Array.from(doc.getElementsByTagName('LineString'));
    for (const ls of lineStrings) {
      const coords = ls.getElementsByTagName('coordinates')[0]?.textContent ?? '';
      points.push(...pointsFromCoordinatesBlob(coords));
    }
  }

  // Fallback: every Placemark \u2192 Point
  if (points.length === 0) {
    const placemarks = Array.from(doc.getElementsByTagName('Placemark'));
    for (const pm of placemarks) {
      const ptCoords = pm.getElementsByTagName('Point')[0]
        ?.getElementsByTagName('coordinates')[0]?.textContent ?? '';
      if (!ptCoords.trim()) continue;
      points.push(...pointsFromCoordinatesBlob(ptCoords));
    }
  }

  return { name: name?.trim() || undefined, points };
}
